import * as Dialog from '@radix-ui/react-dialog';

interface ConfirmDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description?: string;
  confirmLabel?: string;
  variant?: 'destructive' | 'default';
  onConfirm: () => void;
}

export function ConfirmDialog({
  open,
  onOpenChange,
  title,
  description,
  confirmLabel = 'Confirm',
  variant = 'default',
  onConfirm,
}: ConfirmDialogProps) {
  const handleConfirm = () => {
    onConfirm();
    onOpenChange(false);
  };

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        {/* Overlay */}
        <Dialog.Overlay
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.7)',
            backdropFilter: 'blur(4px)',
            WebkitBackdropFilter: 'blur(4px)',
            zIndex: 1000,
          }}
        />

        {/* Content */}
        <Dialog.Content
          style={{
            position: 'fixed',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            background: 'var(--color-surface)',
            borderRadius: 16,
            padding: 24,
            maxWidth: 320,
            width: 'calc(100vw - 32px)',
            zIndex: 1001,
            border: '1px solid var(--color-border)',
            boxShadow: 'var(--shadow-lg)',
          }}
        >
          {/* Title */}
          <Dialog.Title
            style={{
              fontSize: 15,
              fontWeight: 600,
              color: 'var(--color-text)',
              margin: 0,
            }}
          >
            {title}
          </Dialog.Title>

          {/* Description */}
          {description && (
            <Dialog.Description
              style={{
                fontSize: 13,
                color: 'var(--color-text-secondary)',
                marginTop: 6,
                lineHeight: 1.5,
              }}
            >
              {description}
            </Dialog.Description>
          )}

          {/* Buttons */}
          <div
            style={{
              display: 'flex',
              gap: 10,
              marginTop: 24,
            }}
          >
            {/* Cancel */}
            <Dialog.Close asChild>
              <button
                style={{
                  flex: 1,
                  height: 44,
                  borderRadius: 8,
                  border: '1px solid var(--color-border)',
                  background: 'var(--color-surface)',
                  color: 'var(--color-text)',
                  fontSize: 14,
                  fontWeight: 500,
                  cursor: 'pointer',
                  transition: 'background 150ms ease',
                  fontFamily: 'var(--font-body)',
                }}
                onMouseEnter={(e) => {
                  (e.currentTarget as HTMLButtonElement).style.background =
                    'var(--color-surface-hover)';
                }}
                onMouseLeave={(e) => {
                  (e.currentTarget as HTMLButtonElement).style.background =
                    'var(--color-surface)';
                }}
              >
                Cancel
              </button>
            </Dialog.Close>

            {/* Confirm */}
            <button
              onClick={handleConfirm}
              style={
                variant === 'destructive'
                  ? {
                      flex: 1,
                      height: 44,
                      borderRadius: 8,
                      border: '1px solid rgba(248,113,113,0.3)',
                      background: 'rgba(248,113,113,0.15)',
                      color: 'var(--color-error)',
                      fontSize: 14,
                      fontWeight: 500,
                      cursor: 'pointer',
                      transition: 'background 150ms ease',
                      fontFamily: 'var(--font-body)',
                    }
                  : {
                      flex: 1,
                      height: 44,
                      borderRadius: 8,
                      border: 'none',
                      background: 'var(--color-primary)',
                      color: '#1A1A2E',
                      fontSize: 14,
                      fontWeight: 600,
                      cursor: 'pointer',
                      transition: 'background 150ms ease',
                      fontFamily: 'var(--font-body)',
                    }
              }
              onMouseEnter={(e) => {
                const el = e.currentTarget as HTMLButtonElement;
                el.style.background =
                  variant === 'destructive'
                    ? 'rgba(248,113,113,0.25)'
                    : 'var(--color-primary-hover)';
              }}
              onMouseLeave={(e) => {
                const el = e.currentTarget as HTMLButtonElement;
                el.style.background =
                  variant === 'destructive'
                    ? 'rgba(248,113,113,0.15)'
                    : 'var(--color-primary)';
              }}
            >
              {confirmLabel}
            </button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
